<template>
  <div class="data-ingestion-container">
    <div class="page-header">
      <h1 class="text-2xl font-semibold text-gray-900">Data Ingestion</h1>
      <p class="text-gray-600 mt-2">Upload files to extract and import supply chain data</p>
    </div>

    <div class="upload-section">
      <div 
        class="upload-area" 
        :class="{ 'drag-active': isDragging, 'has-file': selectedFile }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleFileDrop"
        @click="triggerFileInput"
      >
        <input 
          type="file" 
          ref="fileInput" 
          class="file-input" 
          @change="handleFileSelect" 
          accept=".csv,.xlsx,.pdf,.docx,.txt,.json"
        >
        
        <div v-if="!selectedFile" class="upload-placeholder">
          <div class="upload-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>
          <h3>Drag & Drop or Click to Upload</h3>
          <p>Supported formats: CSV, Excel, PDF, Word, Text, JSON</p>
        </div>
        
        <div v-else class="file-preview">
          <div class="file-icon" :class="fileIconClass">
            <span>{{ fileExtension.toUpperCase() }}</span>
          </div>
          <div class="file-info">
            <h4>{{ selectedFile.name }}</h4>
            <p>{{ formatFileSize(selectedFile.size) }}</p>
          </div>
          <button class="remove-file" @click.stop="removeFile">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div class="upload-actions" v-if="selectedFile">
        <button 
          class="btn-process" 
          @click="processFile" 
          :disabled="isProcessing"
        >
          <span v-if="!isProcessing">Process File</span>
          <span v-else class="loading-spinner"></span>
        </button>
      </div>
    </div>

    <!-- Processing Status -->
    <div class="processing-status" v-if="processingStatus">
      <div class="status-header">
        <h3>Processing Status</h3>
        <div class="status-badge" :class="statusClass">{{ processingStatus }}</div>
      </div>
      
      <div class="progress-container" v-if="isProcessing">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: `${processingProgress}%` }"></div>
        </div>
        <div class="progress-text">{{ processingProgress }}%</div>
      </div>
      
      <div class="status-message" v-if="statusMessage">
        {{ statusMessage }}
      </div>
    </div>

    <!-- Results Preview (shown after processing) -->
    <div class="results-preview" v-if="extractedData && !isProcessing">
      <h3>Extracted Data Preview</h3>
      
      <div class="entity-tabs">
        <button 
          v-for="(entity, index) in entityTypes" 
          :key="index"
          class="entity-tab"
          :class="{ active: activeEntityTab === entity.type }"
          @click="activeEntityTab = entity.type"
        >
          {{ entity.label }} 
          <span class="count" v-if="getEntityCount(entity.type)">{{ getEntityCount(entity.type) }}</span>
        </button>
      </div>
      
      <div class="entity-content">
        <template v-if="activeEntityTab === 'farm'">
          <farm-data-preview 
            :data="extractedData.farms" 
            @update="updateEntityData('farms', $event)"
          />
        </template>
        
        <template v-else-if="activeEntityTab === 'wool-line'">
          <wool-line-preview 
            :data="extractedData.woolLines" 
            @update="updateEntityData('woolLines', $event)"
          />
        </template>
        
        <template v-else-if="activeEntityTab === 'testing'">
          <testing-preview 
            :data="extractedData.testing" 
            @update="updateEntityData('testing', $event)"
          />
        </template>
        
        <template v-else-if="activeEntityTab === 'auction'">
          <auction-preview 
            :data="extractedData.auctions" 
            @update="updateEntityData('auctions', $event)"
          />
        </template>
        
        <template v-else-if="activeEntityTab === 'export'">
          <export-preview 
            :data="extractedData.exports" 
            @update="updateEntityData('exports', $event)"
          />
        </template>
        
        <template v-else-if="activeEntityTab === 'factory'">
          <factory-preview 
            :data="extractedData.factories" 
            @update="updateEntityData('factories', $event)"
          />
        </template>
        
        <template v-else-if="activeEntityTab === 'brand'">
          <brand-preview 
            :data="extractedData.brands" 
            @update="updateEntityData('brands', $event)"
          />
        </template>
        
        <template v-else>
          <div class="no-data">
            <p>No data extracted for this entity type</p>
          </div>
        </template>
      </div>
      
      <div class="action-buttons">
        <button class="btn-secondary" @click="resetForm">Cancel</button>
        <button class="btn-primary" @click="saveToDatabase" :disabled="isSaving">
          <span v-if="!isSaving">Save to Database</span>
          <span v-else class="loading-spinner"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isDragging: false,
      selectedFile: null,
      isProcessing: false,
      isSaving: false,
      processingStatus: null,
      processingProgress: 0,
      statusMessage: null,
      extractedData: null,
      activeEntityTab: 'farm',
      entityTypes: [
        { type: 'farm', label: 'Farms' },
        { type: 'wool-line', label: 'Wool Lines' },
        { type: 'testing', label: 'Testing' },
        { type: 'auction', label: 'Auctions' },
        { type: 'export', label: 'Exports' },
        { type: 'factory', label: 'Factories' },
        { type: 'brand', label: 'Brands' }
      ]
    };
  },
  
  computed: {
    fileExtension() {
      if (!this.selectedFile) return '';
      return this.selectedFile.name.split('.').pop().toLowerCase();
    },
    
    fileIconClass() {
      const extension = this.fileExtension;
      const iconClasses = {
        'csv': 'csv-icon',
        'xlsx': 'excel-icon',
        'xls': 'excel-icon',
        'pdf': 'pdf-icon',
        'docx': 'word-icon',
        'doc': 'word-icon',
        'txt': 'text-icon',
        'json': 'json-icon'
      };
      
      return iconClasses[extension] || 'default-icon';
    },
    
    statusClass() {
      const statusClasses = {
        'Processing': 'status-processing',
        'Completed': 'status-success',
        'Error': 'status-error',
        'Ready': 'status-ready'
      };
      
      return statusClasses[this.processingStatus] || '';
    }
  },
  
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.isDragging = false;
      }
    },
    
    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.selectedFile = file;
      }
    },
    
    removeFile(event) {
      event.stopPropagation();
      this.selectedFile = null;
      this.extractedData = null;
      this.processingStatus = null;
      this.$refs.fileInput.value = '';
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    async processFile() {
      if (!this.selectedFile) return;
      
      this.isProcessing = true;
      this.processingStatus = 'Processing';
      this.processingProgress = 0;
      this.statusMessage = 'Uploading file...';
      
      try {
        // Create form data for file upload
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          if (this.processingProgress < 90) {
            this.processingProgress += 5;
            
            if (this.processingProgress < 30) {
              this.statusMessage = 'Uploading file...';
            } else if (this.processingProgress < 60) {
              this.statusMessage = 'AI analyzing document content...';
            } else {
              this.statusMessage = 'Mapping data to database schema...';
            }
          }
        }, 500);
        
        let response;
        try {
          response = await this.$axios.post('/api/data-ingestion/process', formData, {
            onUploadProgress: progressEvent => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.uploadProgress = percentCompleted;
            },
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (error) {
          console.error('API request error:', error);
          throw new Error(`API request failed: ${error.response?.data?.message || error.message}`);
        }
        
        clearInterval(progressInterval);
        
        if (response && response.data && response.data.success) {
          this.extractedData = response.data.extractedData;
          this.processingStatus = 'Completed';
          this.processingProgress = 100;
          this.statusMessage = 'Data extraction complete. Please review before saving.';
          
          // Show success toast notification for processing
          try {
            this.$toast.success('Data extraction complete. Please review before saving.', {
              timeout: 4000
            });
            console.log('Process success toast notification sent');
          } catch (toastError) {
            console.error('Error showing toast notification:', toastError);
          }
        } else {
          throw new Error((response && response.data && response.data.message) ? response.data.message : 'Processing failed');
        }
      } catch (error) {
        console.error('Error processing file:', error);
        this.processingStatus = 'Error';
        this.statusMessage = `Error: ${error.message || 'Failed to process file'}`;
        
        // Show error toast notification for processing
        try {
          this.$toast.error(`Error processing file: ${error.message || 'Failed to process file'}`, {
            timeout: 8000 // Longer timeout for errors
          });
          console.log('Process error toast notification sent');
        } catch (toastError) {
          console.error('Error showing toast notification:', toastError);
        }
      } finally {
        this.isProcessing = false;
      }
    },
    
    getEntityCount(entityType) {
      if (!this.extractedData) return 0;
      
      const entityMappings = {
        'farm': 'farms',
        'wool-line': 'woolLines',
        'testing': 'testing',
        'auction': 'auctions',
        'export': 'exports',
        'factory': 'factories',
        'brand': 'brands'
      };
      
      const dataKey = entityMappings[entityType];
      return this.extractedData[dataKey]?.length || 0;
    },
    
    updateEntityData(entityKey, updatedData) {
      if (this.extractedData) {
        this.extractedData[entityKey] = updatedData;
      }
    },
    
    async saveToDatabase() {
      if (!this.extractedData) return;
      
      this.isSaving = true;
      this.statusMessage = 'Saving data to database...';
      
      try {
        let response;
        try {
          response = await this.$axios.post('/api/data-ingestion/save', {
            data: this.extractedData
          });
        } catch (err) {
          throw new Error(`API request failed: ${err.response?.data?.message || err.message}`);
        }
        
        if (response && response.data && response.data.success) {
          // Show success toast notification
          try {
            this.$toast.success('Data successfully saved to database!', {
              timeout: 5000
            });
            console.log('Toast notification sent');
          } catch (toastError) {
            console.error('Error showing toast notification:', toastError);
          }
          
          this.statusMessage = 'Data successfully saved to database!';
          this.$emit('data-saved', response.data.results);
          
          // Reset form after short delay
          setTimeout(() => {
            this.resetForm();
          }, 2000);
        } else {
          throw new Error((response && response.data && response.data.message) ? response.data.message : 'Failed to save data');
        }
      } catch (error) {
        console.error('Error saving data:', error);
        
        // Show error toast notification
        try {
          this.$toast.error(`Error: ${error.message || 'Failed to save data'}`, {
            timeout: 8000 // Longer timeout for errors
          });
          console.log('Error toast notification sent');
        } catch (toastError) {
          console.error('Error showing toast notification:', toastError);
        }
        
        this.statusMessage = `Error: ${error.message || 'Failed to save data'}`;
      } finally {
        this.isSaving = false;
      }
    },
    
    resetForm() {
      this.selectedFile = null;
      this.extractedData = null;
      this.processingStatus = null;
      this.processingProgress = 0;
      this.statusMessage = null;
      this.activeEntityTab = 'farm';
      this.$refs.fileInput.value = '';
    }
  }
};
</script>

<style scoped>
.data-ingestion-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #111827;
}

.text-muted {
  color: #6b7280;
  font-size: 14px;
}

.upload-section {
  margin-bottom: 32px;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f9fafb;
  position: relative;
}

.upload-area:hover {
  border-color: #3A895F;
  background-color: #f3f4f6;
}

.drag-active {
  border-color: #3A895F;
  background-color: rgba(58, 137, 95, 0.05);
}

.has-file {
  border-style: solid;
  background-color: white;
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-icon {
  margin-bottom: 16px;
  color: #3A895F;
}

.upload-placeholder h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #111827;
}

.upload-placeholder p {
  color: #6b7280;
  font-size: 14px;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
}

.file-icon {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  margin-right: 16px;
}

.csv-icon { background-color: #10b981; }
.excel-icon { background-color: #16a34a; }
.pdf-icon { background-color: #ef4444; }
.word-icon { background-color: #2563eb; }
.text-icon { background-color: #6b7280; }
.json-icon { background-color: #f59e0b; }
.default-icon { background-color: #6b7280; }

.file-info {
  flex: 1;
}

.file-info h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #111827;
}

.file-info p {
  color: #6b7280;
  font-size: 14px;
}

.remove-file {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.remove-file:hover {
  color: #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
}

.upload-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.btn-process {
  background-color: #3A895F;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-process:hover {
  background-color: #2f7049;
}

.btn-process:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.processing-status {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.status-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.status-processing {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-success {
  background-color: #d1fae5;
  color: #065f46;
}

.status-error {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-ready {
  background-color: #f3f4f6;
  color: #374151;
}

.progress-container {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background-color: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 12px;
}

.progress-fill {
  height: 100%;
  background-color: #3A895F;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  min-width: 40px;
  text-align: right;
}

.status-message {
  font-size: 14px;
  color: #6b7280;
}

.results-preview {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
}

.results-preview h3 {
  font-size: 18px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 16px;
  color: #111827;
}

.entity-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
  overflow-x: auto;
}

.entity-tab {
  padding: 12px 16px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.entity-tab.active {
  color: #3A895F;
  font-weight: 600;
}

.entity-tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #3A895F;
}

.entity-tab .count {
  display: inline-block;
  background-color: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 9999px;
  margin-left: 6px;
}

.entity-tab.active .count {
  background-color: #d1fae5;
  color: #065f46;
}

.entity-content {
  min-height: 300px;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #6b7280;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 12px;
}

.btn-secondary {
  background-color: white;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.btn-primary {
  background-color: #3A895F;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #2f7049;
}

.btn-primary:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
